import _ from 'lodash'
import Box from '@mui/material/Box'

import { Consultation } from '@/core/models/consultation.model'
import { CustomDevice } from '@/core/models/custom-device.model'
import { Room } from '@/core/models/room.model'

import { MedicalActsSection } from '@/features/medical-acts/ui/MedicalActsSection'

import { DiagnosticRTCProvider } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import RpiProvider from '@/providers/rpi/RpiProvider'

import { getEnv } from '@/utils/helpers'

import Biologicals from '@/pages/diagnostic/containers/Biologicals'
import ConsultationForms from '@/pages/diagnostic/containers/ConsultationForms'
import DiagnosticMedicalVideo from '@/pages/diagnostic/containers/DiagnosticMedicalVideo'
import DiagnosticStethoscope from '@/pages/diagnostic/containers/DiagnosticStethoscope'
import PatientInfo from '@/pages/diagnostic/containers/PatientInfo'
import Payment from '@/pages/diagnostic/containers/Payment'
import RpiMesures from '@/pages/diagnostic/containers/RpiMesures'
import { AllDiagnostics } from '@/pages/diagnostic/containers/AllInOneDiagnostics'
import { LiveTranscriptionSection } from '@/features/live-transcription'
import ContentHeader from '@/pages/diagnostic/components/ContentHeader'
import { useTranslation } from 'react-i18next'

type ContentProps = {
  consultation: Consultation
  consultationId?: string
  selectedMenuItem: string
  isSidebarVisible: boolean
  customDevices: CustomDevice[]
  isStreamer: boolean
  medicalVideoAttendeeInfo?: any
  medicalVideoMeetingInfo?: any
  stethoscopeAttendeeInfo?: any
  stethoscopeMeetingInfo?: any
  peerjsConfig: any
  rpiConfig?: any
  rpiMeetingId?: string
  room: Room
  diagnosticSubTab?: string | null
  onDiagnosticSubTabChange?: () => void
}

export function Content({
  consultation,
  consultationId,
  selectedMenuItem,
  isSidebarVisible,
  customDevices,
  isStreamer,
  medicalVideoAttendeeInfo,
  medicalVideoMeetingInfo,
  stethoscopeAttendeeInfo,
  stethoscopeMeetingInfo,
  peerjsConfig,
  rpiConfig,
  rpiMeetingId,
  room,
  diagnosticSubTab,
  onDiagnosticSubTabChange,
}: ContentProps) {
  const { t } = useTranslation()

  if (!isSidebarVisible) {
    return <ContentHeader sx={{ p: '20px 16px' }} title={t('page.liveConsultation.consultationInfo.title')} />
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto' }}>
      {selectedMenuItem === 'medicalVideo' && (
        <DiagnosticRTCProvider
          config={{
            chime: {
              attendeeInfo: medicalVideoAttendeeInfo,
              meetingInfo: medicalVideoMeetingInfo,
            },
            peerjs: peerjsConfig,
            rpi: rpiConfig,
          }}
          customDevices={customDevices}
          isStreamer={isStreamer}
          type="video"
        >
          <DiagnosticMedicalVideo consultationId={consultationId} />
        </DiagnosticRTCProvider>
      )}
      {selectedMenuItem === 'mesures' && (
        <>
          {rpiMeetingId ? (
            <RpiProvider isStreamer={isStreamer} meetingID={rpiMeetingId} serverUrl={getEnv('VITE_RPI_GATEWAY')!}>
              <RpiMesures isStreamer={isStreamer} consultationId={consultationId} />
            </RpiProvider>
          ) : (
            <AllDiagnostics consultationId={consultationId} isStreamer={isStreamer} activeTabId={diagnosticSubTab} onTabChange={onDiagnosticSubTabChange} />
          )}
        </>
      )}
      {selectedMenuItem === 'biologicals' && <Biologicals consultationId={consultationId} />}
      {selectedMenuItem === 'patientInfo' && <PatientInfo consultationId={consultationId} showHistory />}
      {selectedMenuItem === 'payment' && <Payment consultationId={consultationId} />}
      {selectedMenuItem === 'transcription' && consultationId && <LiveTranscriptionSection consultation={consultation} consultationId={consultationId} />}
      {selectedMenuItem === 'forms' && (
        <Box sx={{ p: 3 }}>
          <ConsultationForms consultationId={consultationId!} />
        </Box>
      )}
      {selectedMenuItem === 'medicalActs' && (
        <Box sx={{ p: 3 }}>
          <MedicalActsSection patientId={consultation.patient._id} consultationId={consultationId} />
        </Box>
      )}
      {selectedMenuItem === 'stethoscope' && (
        <DiagnosticRTCProvider
          config={{
            chime: {
              attendeeInfo: stethoscopeAttendeeInfo,
              meetingInfo: stethoscopeMeetingInfo,
            },
            peerjs: { ...peerjsConfig, streamer: _.replace(room.streamer, /mobile/g, '') },
            rpi: rpiConfig,
          }}
          customDevices={customDevices}
          isStreamer={isStreamer}
          type="audio"
        >
          <DiagnosticStethoscope consultationId={consultationId} />
        </DiagnosticRTCProvider>
      )}
    </Box>
  )
}
